import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
	ViewChild,
	OnInit,
	inject,
	AfterViewInit,
} from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MaterialToAuPaginationDataConverter } from './au-paginator.converter.class';
import {
	IAuPaginationData,
	IDjangoPaginationData,
} from '@shared/ui-components/au-paginator/pagination-page.info';
import { PaginationControlService } from '@app/pages/files/services/pagination-control.service';
import { TranslateService } from '@tolgee/ngx';

/**
 * -->IMPORTANT BLOCK!!!
 *    Do not remove paginator from page when reacting to its update event,
 *    or it will reinitialize, and you'll struggle to death debugging it
 * <--IMPORTANT BLOCK!!!
 *
 * So, how it works. AuPaginator is a converter between material pagiantor
 * which based on amount of pageIndex, length and pageSize.
 * It's like a size of book and number of books page.
 * But our Django backend has pagination based on cursors.
 * It's like a book that has hashes(cursors) and you ask N items before or after this cursor.
 *
 * So on at first you need to provide initials state for first request in parent component.
 * Something like { first: 10 }.
 * Then provide for paginator the same initialPageSize(default value 10)
 * Then on each request you need to save djangoPaginationData and pass it to paginator
 * 		this.djangoPaginationData = {
 * 			pageInfo: data.<entity>.pageInfo,
 * 			totalCount: data.<entity>.totalCount
 * 		};
 * 	/////////////////////////////////////////////
 * 	<au-paginator [nodes]="djangoPaginationData">
 * 	Thats all! paginator will be automatically converting all stuff and returns
 * 	IAuPaginationData type on each action.
 *
 * 	Also you have a resetPaginator$ subject. Use it if you have a filters.
 * 	Cos you need to reset pagination each time filter is changed but not vise versa
 */

@Component({
	selector: 'au-paginator',
	templateUrl: './au-paginator.component.html',
	styleUrls: ['./au-paginator.component.scss'],
	imports: [MatPaginator],
})
export class AuPaginatorComponent
	extends MaterialToAuPaginationDataConverter
	implements OnChanges, OnDestroy, OnInit, AfterViewInit
{
	private readonly paginationControlService = inject(PaginationControlService);
	private readonly translateService = inject(TranslateService);
	@ViewChild(MatPaginator) paginator: MatPaginator;

	@Input() resetPaginator;
	@Input() set initialPageSize(size) {
		this.pageEvent.pageSize = size;
	}
	@Input() nodes: IDjangoPaginationData;
	@Input() pageSizeOptions = [5, 10, 20, 50, 100];

	@Output() paginationEmit = new EventEmitter<IAuPaginationData>();

	pageEvent: PageEvent = {
		length: 0,
		pageIndex: 0,
		pageSize: 10,
		previousPageIndex: 0,
	};

	private startCursor: string;
	private endCursor: string;
	private destroy$ = new Subject<void>();

	constructor() {
		super();
	}

	ngOnInit() {
		if (this.resetPaginator) {
			this.resetPaginator.pipe(takeUntil(this.destroy$)).subscribe(() => {
				this.resetPageIndex();
			});
		}
		this.subscribeToResetPaginator();
	}

	ngAfterViewInit() {
		if (this.paginator) {
			this.paginator._intl.itemsPerPageLabel =
				this.translateService.instant('items_per_page');

			this.paginator._intl.getRangeLabel = (
				page: number,
				pageSize: number,
				length: number
			) => {
				if (length === 0 || pageSize === 0) {
					return `0 ${this.translateService.instant('of_text')} ${length}`;
				}

				length = Math.max(length, 0);
				const startIndex = page * pageSize;
				const endIndex =
					startIndex < length
						? Math.min(startIndex + pageSize, length)
						: startIndex + pageSize;

				return `${startIndex + 1} – ${endIndex} ${this.translateService.instant('of_text')} ${length}`;
			};
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.nodes?.currentValue) {
			const value = changes.nodes.currentValue;
			this.startCursor = value.pageInfo.startCursor;
			this.endCursor = value.pageInfo.endCursor;
			this.pageEvent.length = value.totalCount;
		}
	}

	paginationChanged(paginationData: PageEvent) {
		if (this.pageSizeChanged(paginationData)) {
			this.resetPageIndex();
			this.pageEvent.pageSize = paginationData.pageSize;
		} else {
			this.pageEvent = paginationData;
		}

		this.paginationEmit.emit(this.paginationData);
	}

	subscribeToResetPaginator() {
		this.paginationControlService.resetPaginationCalled
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => this.resetPageIndex());
	}

	private resetPageIndex(): void {
		this.pageEvent.pageIndex = 0;
		this.pageEvent.previousPageIndex = 0;
		this.startCursor = null;
		this.endCursor = null;
		if (this.paginator) {
			this.paginator.pageIndex = 0;
		}
	}

	private get paginationData(): IAuPaginationData {
		return this.convertToAuPaginationData(
			{
				...this.pageEvent,
			},
			{
				firstNodeCursor: this.startCursor,
				lastNodeCursor: this.endCursor,
			}
		);
	}

	private pageSizeChanged(pageEvent: PageEvent): boolean {
		return this.pageEvent.pageSize !== pageEvent.pageSize;
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
